import { Injectable, effect, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';

export type MenuMode = 'static' | 'overlay' | 'slim-plus' | 'slim' | 'horizontal' | 'reveal' | 'drawer';

// Experimental navigation switch: 'classic' = current sidebar, 'new' = the
// sidebar with the "Your site" entity group. Temporary — remove once one wins.
export type NavVariant = 'classic' | 'new';
const NAV_VARIANT_KEY = 'cb.navVariant';
// Persisted desktop-static sidebar collapse preference (the topbar collapse toggle). Transient hover
// (sidebarExpanded) and overlay/mobile menu state are deliberately NOT persisted.
const SIDEBAR_COLLAPSED_KEY = 'cb.sidebarCollapsed';

export interface LayoutConfig {
    preset: string;
    primary: string;
    surface: string | undefined | null;
    darkTheme: boolean;
    menuMode: MenuMode;
    menuTheme: string;
    topbarTheme: string;
    menuProfilePosition: string;
}

export interface LayoutState {
    staticMenuDesktopInactive: boolean;
    overlayMenuActive: boolean;
    profileSidebarVisible: boolean;
    configSidebarVisible: boolean;
    mobileMenuActive: boolean;
    searchBarActive: boolean;
    sidebarExpanded: boolean;
    menuHoverActive: boolean;
    activePath: any;
    anchored: boolean;
    topbarMenuActive: boolean;
    menuProfileActive: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    layoutConfig = signal<LayoutConfig>({
        preset: 'Material',
        primary: 'indigo',
        surface: 'slate',
        darkTheme: false,
        menuMode: 'static',
        menuTheme: 'light',
        topbarTheme: 'indigo',
        menuProfilePosition: 'end'
    });

    layoutState = signal<LayoutState>({
        staticMenuDesktopInactive: this.readSidebarCollapsed(),
        overlayMenuActive: false,
        profileSidebarVisible: false,
        configSidebarVisible: false,
        mobileMenuActive: false,
        searchBarActive: false,
        sidebarExpanded: false,
        menuHoverActive: false,
        activePath: null,
        anchored: false,
        topbarMenuActive: false,
        menuProfileActive: false
    });

    router = inject(Router);

    // Experimental nav switch, persisted so the choice survives reloads.
    readonly navVariant = signal<NavVariant>(this.readNavVariant());

    isDarkTheme = computed(() => this.layoutConfig().darkTheme);

    isSlim = computed(() => this.layoutConfig().menuMode === 'slim');

    isSlimPlus = computed(() => this.layoutConfig().menuMode === 'slim-plus');

    isHorizontal = computed(() => this.layoutConfig().menuMode === 'horizontal');

    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    hasOverlaySubmenu = computed(() => this.isSlim() || this.isSlimPlus() || this.isHorizontal());

    hasOpenOverlay = computed(() => this.layoutState().overlayMenuActive || this.hasOpenOverlaySubmenu());

    hasOpenOverlaySubmenu = computed(() => {
        return this.hasOverlaySubmenu() && !!this.layoutState().activePath;
    });

    isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().mobileMenuActive);

    isSidebarStateChanged = computed(() => {
        const layoutConfig = this.layoutConfig();
        return layoutConfig.menuMode === 'horizontal' || layoutConfig.menuMode === 'slim' || layoutConfig.menuMode === 'slim-plus';
    });

    private initialized = false;

    private previousMenuMode: MenuMode | undefined = undefined;

    constructor() {
        effect(() => {
            const config = this.layoutConfig();

            if (!this.initialized || !config) {
                this.initialized = true;
                return;
            }

            this.handleDarkModeTransition(config);
        });

        effect(() => {
            this.updateMenuState();
        });
    }

    private updateMenuState() {
        const menuMode = this.layoutConfig().menuMode;
        if (this.previousMenuMode === undefined) {
            this.previousMenuMode = menuMode;
            return;
        }

        if (this.previousMenuMode === menuMode) {
            return;
        }

        this.previousMenuMode = menuMode;

        const isOverlaySubmenu = menuMode === 'slim' || menuMode === 'slim-plus' || menuMode === 'horizontal';

        this.layoutState.update((prev) => ({
            ...prev,
            // Persisted collapse preference survives a menuMode change (was hardcoded false).
            staticMenuDesktopInactive: this.readSidebarCollapsed(),
            overlayMenuActive: false,
            staticMenuMobileActive: false,
            sidebarExpanded: false,
            menuHoverActive: false,
            anchored: false,
            menuProfileActive: false,
            activePath: this.isDesktop() ? (isOverlaySubmenu ? null : this.router.url) : prev.activePath
        }));
    }

    private handleDarkModeTransition(config: LayoutConfig): void {
        const supportsViewTransition = 'startViewTransition' in document;

        if (supportsViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode(config);
        }
    }

    private startViewTransition(config: LayoutConfig): void {
        (document as any).startViewTransition(() => {
            this.toggleDarkMode(config);
        });
    }

    toggleDarkMode(config?: LayoutConfig): void {
        const _config = config || this.layoutConfig();
        if (_config.darkTheme) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
    }

    onMenuToggle() {
        if (this.isDesktop()) {
            if (this.layoutConfig().menuMode === 'static') {
                const collapsed = !this.layoutState().staticMenuDesktopInactive;
                this.writeSidebarCollapsed(collapsed);
                this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: collapsed }));
            }

            if (this.layoutConfig().menuMode === 'overlay') {
                this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !prev.overlayMenuActive }));
            }
        } else {
            this.layoutState.update((prev) => ({ ...prev, mobileMenuActive: !prev.mobileMenuActive }));
        }
    }

    onMenuProfileToggle() {
        this.layoutState.update((prev) => ({ ...prev, menuProfileActive: !prev.menuProfileActive }));
    }

    toggleConfigSidebar() {
        this.layoutState.update((prev) => ({
            ...prev,
            configSidebarVisible: !prev.configSidebarVisible
        }));
    }

    hideConfigSidebar() {
        this.layoutState.update((prev) => ({ ...prev, configSidebarVisible: false }));
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    setNavVariant(variant: NavVariant): void {
        this.navVariant.set(variant);
        try {
            localStorage.setItem(NAV_VARIANT_KEY, variant);
        } catch {
            // Ignore storage failures (private mode, quota); the signal still updates.
        }
    }

    private writeSidebarCollapsed(collapsed: boolean): void {
        try {
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? 'true' : 'false');
        } catch {
            // Ignore storage failures (private mode, quota); the signal still updates.
        }
    }

    private readSidebarCollapsed(): boolean {
        // Default expanded (false) for any session without an explicit choice (fresh browser,
        // cleared storage, or garbage value). Only an explicitly-stored 'true' collapses on load.
        try {
            return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
        } catch {
            return false;
        }
    }

    private readNavVariant(): NavVariant {
        // Default to 'new' for any session without an explicit choice (fresh browser,
        // cleared storage, or garbage value). Only an explicitly-stored 'classic' — set
        // via the configurator toggle — opts back into the classic sidebar. The toggle
        // stays available for comparison/rollback; only the default flipped.
        try {
            return localStorage.getItem(NAV_VARIANT_KEY) === 'classic' ? 'classic' : 'new';
        } catch {
            return 'new';
        }
    }
}
